import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { clientesApi, presupuestosApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ArrowRight, 
  Plus, 
  Trash2,
  User,
  FileText,
  CheckCircle,
  Send
} from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Cliente', icon: User },
  { id: 2, title: 'Items', icon: FileText },
  { id: 3, title: 'Términos', icon: CheckCircle },
  { id: 4, title: 'Vista Previa', icon: Send },
];

export default function NuevoPresupuestoPage() {
  const { empresaActual } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  
  // Form state
  const [clienteId, setClienteId] = useState('');
  const [nuevoCliente, setNuevoCliente] = useState({ nombre: '', email: '', telefono: '', empresa_cliente: '' });
  const [crearNuevoCliente, setCrearNuevoCliente] = useState(false);
  const [items, setItems] = useState([{ descripcion: '', cantidad: 1, precio_unitario: 0 }]);
  const [validezDias, setValidezDias] = useState(30);
  const [formaPago, setFormaPago] = useState('Transferencia bancaria');
  const [notas, setNotas] = useState('');

  useEffect(() => {
    const loadClientes = async () => {
      if (!empresaActual?.id) return;
      try {
        const response = await clientesApi.getAll(empresaActual.id);
        setClientes(response.data);
      } catch (error) {
        console.error('Error loading clientes:', error);
      }
    };
    loadClientes();
  }, [empresaActual?.id]);

  const calcularTotales = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.cantidad * item.precio_unitario), 0);
    const iva = subtotal * 0.21;
    const total = subtotal + iva;
    return { subtotal, iva, total };
  };

  const handleAddItem = () => {
    setItems([...items, { descripcion: '', cantidad: 1, precio_unitario: 0 }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === 'descripcion' ? value : parseFloat(value) || 0;
    setItems(newItems);
  };

  const canContinue = () => {
    switch (step) {
      case 1:
        if (crearNuevoCliente) {
          return nuevoCliente.nombre && nuevoCliente.email;
        }
        return clienteId;
      case 2:
        return items.every(item => item.descripcion && item.cantidad > 0 && item.precio_unitario > 0);
      case 3:
        return validezDias > 0 && formaPago;
      default:
        return true;
    }
  };

  const handleSubmit = async (enviar = false) => {
    setLoading(true);
    
    try {
      let finalClienteId = clienteId;
      
      // Create new client if needed
      if (crearNuevoCliente) {
        const clienteResponse = await clientesApi.create({
          ...nuevoCliente,
          empresa_id: empresaActual.id
        });
        finalClienteId = clienteResponse.data.id;
      }
      
      // Create presupuesto
      const presupuestoData = {
        empresa_id: empresaActual.id,
        cliente_id: finalClienteId,
        items: items.map(item => ({
          descripcion: item.descripcion,
          cantidad: item.cantidad,
          precio_unitario: item.precio_unitario,
          total: item.cantidad * item.precio_unitario
        })),
        validez_dias: validezDias,
        forma_pago: formaPago,
        notas: notas || null
      };
      
      const response = await presupuestosApi.create(presupuestoData);
      
      if (enviar) {
        await presupuestosApi.enviar(response.data.id);
        toast.success('Presupuesto creado y enviado');
      } else {
        toast.success('Presupuesto guardado como borrador');
      }
      
      navigate('/presupuestos');
    } catch (error) {
      console.error('Error creating presupuesto:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el presupuesto');
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, iva, total } = calcularTotales();
  const selectedCliente = clientes.find(c => c.id === clienteId);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="nuevo-presupuesto-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/presupuestos')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nuevo Presupuesto
            </h1>
          </div>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-between">
          {STEPS.map((s, index) => (
            <div key={s.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div className={`wizard-step ${step > s.id ? 'completed' : step === s.id ? 'current' : 'upcoming'}`}>
                  {step > s.id ? <CheckCircle className="w-4 h-4" /> : s.id}
                </div>
                <span className={`text-xs mt-1 ${step >= s.id ? 'text-slate-900' : 'text-slate-400'}`}>
                  {s.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`w-16 md:w-24 h-0.5 mx-2 ${step > s.id ? 'bg-blue-600' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card>
          <CardContent className="p-6">
            {/* Step 1: Cliente */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Selecciona o crea un cliente
                  </h2>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <Button
                      variant={!crearNuevoCliente ? 'default' : 'outline'}
                      onClick={() => setCrearNuevoCliente(false)}
                      className={!crearNuevoCliente ? 'bg-blue-600' : ''}
                    >
                      Cliente existente
                    </Button>
                    <Button
                      variant={crearNuevoCliente ? 'default' : 'outline'}
                      onClick={() => setCrearNuevoCliente(true)}
                      className={crearNuevoCliente ? 'bg-blue-600' : ''}
                    >
                      Nuevo cliente
                    </Button>
                  </div>
                  
                  {!crearNuevoCliente ? (
                    <Select value={clienteId} onValueChange={setClienteId}>
                      <SelectTrigger data-testid="select-cliente">
                        <SelectValue placeholder="Selecciona un cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientes.map(cliente => (
                          <SelectItem key={cliente.id} value={cliente.id}>
                            {cliente.nombre} {cliente.empresa_cliente && `- ${cliente.empresa_cliente}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre *</Label>
                        <Input
                          value={nuevoCliente.nombre}
                          onChange={(e) => setNuevoCliente({ ...nuevoCliente, nombre: e.target.value })}
                          placeholder="Nombre completo"
                          data-testid="nuevo-cliente-nombre"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={nuevoCliente.email}
                          onChange={(e) => setNuevoCliente({ ...nuevoCliente, email: e.target.value })}
                          placeholder="email@ejemplo.com"
                          data-testid="nuevo-cliente-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Teléfono</Label>
                        <Input
                          value={nuevoCliente.telefono}
                          onChange={(e) => setNuevoCliente({ ...nuevoCliente, telefono: e.target.value })}
                          placeholder="+34 600 000 000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Empresa</Label>
                        <Input
                          value={nuevoCliente.empresa_cliente}
                          onChange={(e) => setNuevoCliente({ ...nuevoCliente, empresa_cliente: e.target.value })}
                          placeholder="Nombre de la empresa"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Items */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Añade los conceptos del presupuesto
                </h2>
                
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-4 items-end p-4 bg-slate-50 rounded-lg">
                      <div className="col-span-12 md:col-span-5 space-y-2">
                        <Label>Descripción *</Label>
                        <Input
                          value={item.descripcion}
                          onChange={(e) => handleItemChange(index, 'descripcion', e.target.value)}
                          placeholder="Descripción del concepto"
                          data-testid={`item-descripcion-${index}`}
                        />
                      </div>
                      <div className="col-span-4 md:col-span-2 space-y-2">
                        <Label>Cantidad *</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.cantidad}
                          onChange={(e) => handleItemChange(index, 'cantidad', e.target.value)}
                          data-testid={`item-cantidad-${index}`}
                        />
                      </div>
                      <div className="col-span-4 md:col-span-2 space-y-2">
                        <Label>Precio Unit. *</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.precio_unitario}
                          onChange={(e) => handleItemChange(index, 'precio_unitario', e.target.value)}
                          data-testid={`item-precio-${index}`}
                        />
                      </div>
                      <div className="col-span-3 md:col-span-2 text-right">
                        <Label className="block mb-2">Total</Label>
                        <span className="font-medium">{formatCurrency(item.cantidad * item.precio_unitario)}</span>
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveItem(index)}
                          disabled={items.length === 1}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <Button variant="outline" onClick={handleAddItem} data-testid="add-item-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir concepto
                </Button>
                
                {/* Totals */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>IVA (21%)</span>
                    <span>{formatCurrency(iva)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-slate-900">
                    <span>Total</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Términos */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Condiciones del presupuesto
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Validez (días) *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={validezDias}
                      onChange={(e) => setValidezDias(parseInt(e.target.value) || 30)}
                      data-testid="validez-dias"
                    />
                    <p className="text-sm text-slate-500">
                      El presupuesto será válido durante {validezDias} días desde su envío
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Forma de pago *</Label>
                    <Select value={formaPago} onValueChange={setFormaPago}>
                      <SelectTrigger data-testid="forma-pago">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Transferencia bancaria">Transferencia bancaria</SelectItem>
                        <SelectItem value="Efectivo">Efectivo</SelectItem>
                        <SelectItem value="Tarjeta de crédito">Tarjeta de crédito</SelectItem>
                        <SelectItem value="50% inicio, 50% entrega">50% inicio, 50% entrega</SelectItem>
                        <SelectItem value="Pago a 30 días">Pago a 30 días</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Notas adicionales</Label>
                  <Textarea
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    placeholder="Añade cualquier información adicional para el cliente..."
                    rows={4}
                    data-testid="notas"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Preview */}
            {step === 4 && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Vista previa del presupuesto
                </h2>
                
                <div className="quote-preview">
                  <div className="quote-header">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {empresaActual?.nombre_comercial || 'Tu Empresa'}
                        </h3>
                        {empresaActual?.direccion && (
                          <p className="text-slate-500 text-sm mt-1">{empresaActual.direccion}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Presupuesto</p>
                        <p className="font-mono text-slate-900">#BORRADOR</p>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-slate-100">
                      <p className="text-sm text-slate-500">Para:</p>
                      <p className="font-medium text-slate-900">
                        {crearNuevoCliente ? nuevoCliente.nombre : selectedCliente?.nombre}
                      </p>
                      <p className="text-slate-600">
                        {crearNuevoCliente ? nuevoCliente.email : selectedCliente?.email}
                      </p>
                    </div>
                  </div>
                  
                  <div className="quote-items">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 text-slate-500 text-sm font-medium">Concepto</th>
                          <th className="text-right py-2 text-slate-500 text-sm font-medium">Cant.</th>
                          <th className="text-right py-2 text-slate-500 text-sm font-medium">Precio</th>
                          <th className="text-right py-2 text-slate-500 text-sm font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={index} className="border-b border-slate-100">
                            <td className="py-3">{item.descripcion}</td>
                            <td className="py-3 text-right">{item.cantidad}</td>
                            <td className="py-3 text-right">{formatCurrency(item.precio_unitario)}</td>
                            <td className="py-3 text-right font-medium">{formatCurrency(item.cantidad * item.precio_unitario)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="quote-footer">
                    <div className="flex justify-end">
                      <div className="w-64 space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Subtotal</span>
                          <span>{formatCurrency(subtotal)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">IVA (21%)</span>
                          <span>{formatCurrency(iva)}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold pt-2 border-t">
                          <span>Total</span>
                          <span>{formatCurrency(total)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-slate-200 text-sm text-slate-500">
                      <p><strong>Validez:</strong> {validezDias} días</p>
                      <p><strong>Forma de pago:</strong> {formaPago}</p>
                      {notas && <p className="mt-2"><strong>Notas:</strong> {notas}</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(step - 1)}
            disabled={step === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Anterior
          </Button>
          
          <div className="flex gap-2">
            {step === 4 ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleSubmit(false)}
                  disabled={loading}
                  data-testid="save-draft-btn"
                >
                  Guardar Borrador
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => handleSubmit(true)}
                  disabled={loading}
                  data-testid="send-quote-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? 'Enviando...' : 'Enviar Presupuesto'}
                </Button>
              </>
            ) : (
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setStep(step + 1)}
                disabled={!canContinue()}
                data-testid="next-step-btn"
              >
                Siguiente
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
